'use client';

import React, { useState, useEffect } from 'react';
import { movementRegistrationService } from '@/lib/services/movementRegistrationService';

interface MovementRegistrationPopupProps {
  sourcePage: string;
  delayMs?: number;
  isOpen?: boolean;
  onClose?: () => void;
}

const MovementRegistrationPopup: React.FC<MovementRegistrationPopupProps> = ({
  sourcePage,
  delayMs = 4000,
  isOpen,
  onClose,
}) => {
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    country: '',
    motivation: '',
  });

  // Auto-timer trigger (existing behaviour)
  useEffect(() => {
    if (isOpen !== undefined) return; // skip auto-timer when controlled externally
    const storageKey = `sig_popup_dismissed_${sourcePage}`;
    const dismissed = sessionStorage.getItem(storageKey);
    if (dismissed) return;

    const timer = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(timer);
  }, [sourcePage, delayMs, isOpen]);

  // External control: sync visible with isOpen prop
  useEffect(() => {
    if (isOpen !== undefined) {
      setVisible(isOpen);
      if (isOpen) {
        // Reset form state when opened externally
        setSubmitted(false);
        setError('');
      }
    }
  }, [isOpen]);

  const handleDismiss = () => {
    if (isOpen !== undefined) {
      // Externally controlled — call parent's onClose
      onClose?.();
    } else {
      sessionStorage.setItem(`sig_popup_dismissed_${sourcePage}`, '1');
    }
    setVisible(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim()) {
      setError('Please enter your name and email.');
      return;
    }
    setLoading(true);
    const result = await movementRegistrationService.register({
      fullName: form.fullName,
      email: form.email,
      country: form.country,
      motivation: form.motivation,
      sourcePage,
    });
    setLoading(false);
    if (result.success) {
      setSubmitted(true);
      setTimeout(() => {
        if (isOpen !== undefined) {
          onClose?.();
        } else {
          sessionStorage.setItem(`sig_popup_dismissed_${sourcePage}`, '1');
        }
        setVisible(false);
      }, 3000);
    } else {
      setError(result.error || 'Registration failed. Please try again.');
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-4 text-white flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-4 text-white/70 hover:text-white text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-3xl">🕊️</span>
            <h2 className="text-lg sm:text-xl font-bold font-headline">Join the No Enemy Movement</h2>
          </div>
          <p className="text-white/90 text-sm">
            Be part of a global transformation. Register today and help build a world without enemies.
          </p>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          {submitted ? (
            <div className="text-center py-6">
              <div className="text-5xl mb-3">🎉</div>
              <h3 className="text-xl font-bold text-foreground mb-2">Welcome to the Movement!</h3>
              <p className="text-muted-foreground text-sm">
                You are now part of a global community committed to peace and transformation.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Country</label>
                <input
                  type="text"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  placeholder="Your country"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Why do you want to join? <span className="text-muted-foreground text-xs">(optional)</span>
                </label>
                <textarea
                  name="motivation"
                  value={form.motivation}
                  onChange={handleChange}
                  placeholder="Share your motivation..."
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                />
              </div>

              {error && (
                <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {loading ? 'Registering...' : '🌍 Register for the Movement'}
              </button>

              <button
                type="button"
                onClick={handleDismiss}
                className="w-full py-2 text-muted-foreground text-xs hover:text-foreground transition-colors"
              >
                Maybe later
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default MovementRegistrationPopup;
